<script setup>
import { computed,onBeforeUnmount,reactive,ref,watch } from 'vue'
import { cloudApi } from '@/api/index.js'
import { authStore } from '@/stores/auth.js'
import { publicationGrantBody } from '@/utils/publicationGrant.js'
const props=defineProps({tenantId:String,projectId:String})
const grants=ref([]),accounts=ref([]),error=ref(''),notice=ref(''),busy=ref(false),pending=ref(null)
const form=reactive({reference:'',version:'',price:'',rounds:'',requests:'',lookback:'',start:'',end:'',failure:'',accounts:[],metrics:false,comments:false,confirmed:false,enabled:false})
const canManage=computed(()=>['owner','admin'].includes(authStore.state.selectedTenant?.role_code))
const supported=new Set(['xiaohongshu','douyin','tiktok','wechat_official','wechat_channels'])
const fields=[['reference','线下收款凭据','text'],['version','本次价格版本','text'],['price','每账号每轮价格（元）','text'],['rounds','总检查轮数（1至1000000）','number'],['requests','每轮请求上限（1至100，含翻页与评论）','number'],['lookback','回看天数（1至365）','number'],['start','服务开始（按本机时间）','datetime-local'],['end','服务结束（按本机时间）','datetime-local']]
const scope=()=>JSON.stringify([props.tenantId,props.projectId,authStore.state.user?.id])
const storageKey=()=>`publication-grant-pending:${scope()}`
const base=()=>`/api/tenants/${encodeURIComponent(props.tenantId)}/projects/${encodeURIComponent(props.projectId)}`
let sequence=0,disposed=false
async function load() {
  const owner=scope(),ticket=++sequence
  try {
    const [service,rows]=await Promise.all([cloudApi.get(base()+'/publication-service'),cloudApi.get(base()+'/social-accounts')])
    if(!disposed && ticket===sequence && owner===scope()) {
      grants.value=service.grants;accounts.value=rows.filter(a=>a.active && supported.has(a.platform));error.value=''
    }
  }catch(e){if(!disposed && owner===scope())error.value=e.message}
}
async function create() {
  if(!canManage.value || busy.value)return
  const owner=scope(),key=storageKey()
  busy.value=true;error.value='';notice.value=''
  try {
    if(!pending.value) {
      const operation={owner,key:crypto.randomUUID(),body:publicationGrantBody(form)}
      localStorage.setItem(key,JSON.stringify(operation));pending.value=operation
    }
    const operation=pending.value
    if(operation.owner!==owner)throw new Error('项目已改变，请回到原项目核对开通结果。')
    await cloudApi.post(base()+'/publication-service/grants',operation.body,{headers:{'Idempotency-Key':operation.key}})
    localStorage.removeItem(key)
    if(!disposed && owner===scope()){pending.value=null;notice.value='已保存企业开通记录。用户仍需在App选择周期并开启。';await load()}
  }catch(e){
    if(!disposed && owner===scope()) {
      error.value=e.message || '开通结果待核对，请重试同一笔操作。'
      if([400,403,422].includes(e.status)){localStorage.removeItem(key);pending.value=null}
    }
  }finally{if(owner===scope())busy.value=false}
}
async function close(grant) {
  if(busy.value || !canManage.value)return
  const owner=scope();busy.value=true
  try {
    await cloudApi.post(base()+`/publication-service/grants/${encodeURIComponent(grant.grant_id)}/close`,{})
    if(owner===scope()){notice.value='服务已关闭，历史记录保留；已发出的请求按原规则结算。';await load()}
  }catch(e){if(owner===scope())error.value=e.message}
  finally{if(owner===scope())busy.value=false}
}
watch(scope,()=>{
  sequence++;grants.value=[];accounts.value=[];error.value='';notice.value='';busy.value=false
  Object.assign(form,{reference:'',version:'',price:'',rounds:'',requests:'',lookback:'',start:'',end:'',failure:'',accounts:[],metrics:false,comments:false,confirmed:false,enabled:false})
  try{pending.value=JSON.parse(localStorage.getItem(storageKey()) || 'null')}
  catch{pending.value=null;error.value='本机待核对记录无法读取，请先核对后台开通记录。'}
  load()
},{immediate:true})
onBeforeUnmount(()=>{disposed=true;sequence++})
</script>
<template>
  <details class="service"><summary>自动核验服务与费用</summary>
    <p>自行勾选免费。自动查询由企业开通额度，用户再设置周期；这里不在线收款。</p>
    <button type="button" :disabled="busy" @click="load">刷新服务记录</button>
    <p v-if="error" role="alert">{{ error }}</p><p v-if="notice" role="status">{{ notice }}</p>
    <ul><li v-for="grant in grants" :key="grant.grant_id"><strong>{{ grant.price_version }} · {{ grant.enabled ? '权益可用' : '未启用或已关闭' }}</strong><span>每账号每轮¥{{ grant.price_microcny/1000000 }} · 剩余{{ grant.remaining_rounds }}轮 · 已用{{ grant.used_rounds }}轮 · 处理中{{ grant.reserved_rounds }}轮；到期 {{ new Date(grant.expires_at).toLocaleString() }}</span><button v-if="canManage && grant.enabled" type="button" :disabled="busy" @click="close(grant)">关闭此服务权益</button></li></ul>
    <form v-if="canManage" @submit.prevent="create">
      <fieldset :disabled="busy || !!pending"><legend>线下确认后开通一笔额度</legend>
        <label v-for="field in fields" :key="field[0]">{{ field[1] }}<input v-model="form[field[0]]" :type="field[2]" required></label>
        <label>失败计费规则<select v-model="form.failure" required><option value="">请选择已约定规则</option><option value="charge_on_complete">完成检查才计费</option><option value="charge_on_attempt">开始查询后即计费</option></select></label>
        <label><input v-model="form.metrics" type="checkbox">包含效果数据</label><label><input v-model="form.comments" type="checkbox">包含首屏评论样本，最多60条</label>
        <p>选择本次开通的用户账号；同平台不同ID分别计费。</p>
        <label v-for="account in accounts" :key="account.platform_account_id"><input v-model="form.accounts" :value="account.platform_account_id" type="checkbox">{{ account.platform }} · {{ account.display_name || account.platform_account_identifier }} · {{ account.platform_account_identifier }}</label>
        <label><input v-model="form.confirmed" type="checkbox" required>已在系统外确认真实收款及上述约定</label>
        <label><input v-model="form.enabled" type="checkbox">立即授权这些账号使用额度（仍由用户开启周期检查）</label>
      </fieldset>
      <button :disabled="busy">{{ busy ? '正在核对…' : pending ? '重试核对同一笔开通' : '保存已确认的企业额度' }}</button>
    </form>
  </details>
</template>
<style scoped>
.service{padding:16px;background:#f5f9f8;border-radius:12px;display:grid;gap:12px}summary{font-weight:700;cursor:pointer}p{line-height:1.7}fieldset{display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:14px;border:1px solid #c7dada;padding:16px}label,li{display:grid;gap:8px;overflow-wrap:anywhere}input,select,button{min-height:40px;padding:8px;border:1px solid #c7dada;border-radius:8px;box-sizing:border-box;max-width:100%}input[type=checkbox]{min-height:20px;width:20px}button{background:#098c86;color:white;cursor:pointer}button:disabled{opacity:.5}ul{padding:0;list-style:none;display:grid;gap:12px}
</style>
